import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCanAccess, useUserPermissions, usePermissionsLoading } from "@/modules/shared/hooks/usePermissions";
import { useAuth } from "@/modules/shared/hooks/useAuth";
import { useModuleAccess } from "@/modules/shared/hooks/useModuleAccess";
import { TrialExpiredGuard } from "@/modules/shared/components/TrialExpiredGuard";

// Map routes to feature keys.
// /dashboard intencionalmente fora desta lista — sempre acessível para
// qualquer utilizador autenticado, mostra um estado vazio amigável quando
// não há módulos atribuídos. Sem isto, utilizadores sem módulos eram
// redirecionados para /opencode/dashboard, o que era confuso para quem
// não usa o championship.
const ROUTE_PERMISSIONS: Record<string, string> = {
  "/clientes": "clientes",
  "/profissionais": "profissionais",
  "/alocacoes": "alocacoes", // redirect handled in App.tsx
  "/historico-alocacoes": "alocacoes",
  "/squads": "squad",
  "/squad-operacoes": "squad",
  "/dashboard-engajamento": "painel_gestores",
  "/checkins": "checkins",
  "/planos-de-acao": "planos_acao",
  "/pdi": "pdi",
  "/resumo-mensal": "resumo_mensal",
  "/cultura": "cultura",
  "/cultura/configurar": "cultura_config",
  // Timesheet
  "/timesheet": "timesheet",
  "/timesheet/semanal": "timesheet_weekly",
  "/timesheet/calendario": "timesheet_calendar",
  "/timesheet/all-times": "timesheet_all_times",
  // Comercial
  "/vagas": "vagas",
  "/carreiras/editor": "vagas",
  "/rate-card": "rate_card",
  "/calculadora-comercial": "calculadora",
  "/financeiro": "financeiro",
  "/reembolsos": "reembolsos",
  "/minha-carteira": "minha_carteira",
  // Operações
  "/projetos": "projetos",
  
  // Recrutamento
  "/recrutamento": "recrutamento",
  // Sistema
  "/configuracoes": "configuracoes",
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  featureKey?: string;
}

export function ProtectedRoute({ children, featureKey }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { loading: moduleLoading } = useModuleAccess();
  
  // Get feature key from route if not provided
  const routeFeatureKey = featureKey || getFeatureKeyFromPath(location.pathname);
  const hasAccess = useCanAccess(routeFeatureKey || "");
  const allPermissions = useUserPermissions();
  const permissionsLoading = usePermissionsLoading();

  // Find first accessible route for redirect
  const getFirstAccessibleRoute = (): string => {
    const routeOrder = [
      "/clientes",
      "/profissionais", 
      "/squads",
      "/projetos",
      "/checkins",
      "/planos-de-acao",
      
      "/rate-card",
      "/calculadora-comercial",
      "/timesheet",
      "/vagas",
      "/recrutamento",
      "/financeiro",
      "/reembolsos",
      "/configuracoes",
    ];
    
    for (const route of routeOrder) {
      const featureKey = ROUTE_PERMISSIONS[route];
      if (featureKey && allPermissions[featureKey]) {
        return route;
      }
    }
    return null; // No accessible route found
  };

  useEffect(() => {
    // Wait for auth and permissions to load
    if (authLoading || permissionsLoading || moduleLoading) return;

    // If not authenticated, redirect to login
    if (!user) {
      navigate("/auth", { replace: true });
      return;
    }

    // Utilizadores sem módulo de negócio caem no /dashboard — a página
    // mostra um estado "sem módulos atribuídos, peça ao admin". Não
    // redirecionamos automaticamente para /opencode/dashboard porque o
    // championship não é o caminho default para todas as orgs.

    // If feature key exists and user doesn't have access
    if (routeFeatureKey && !hasAccess) {
      const firstRoute = getFirstAccessibleRoute();
      navigate(firstRoute || "/dashboard", { replace: true });
    }
  }, [authLoading, permissionsLoading, moduleLoading, user, routeFeatureKey, hasAccess, navigate, location.pathname, allPermissions]);

  // Show nothing while checking auth, permissions or modules
  if (authLoading || permissionsLoading || moduleLoading) {
    return null;
  }

  // If no user, don't render (will redirect)
  if (!user) {
    return null;
  }

  // If feature key exists and no access, don't render (will redirect)
  if (routeFeatureKey && !hasAccess) {
    return null;
  }

  return <TrialExpiredGuard>{children}</TrialExpiredGuard>;
}

function getFeatureKeyFromPath(pathname: string): string | null {
  // Check exact match first
  if (ROUTE_PERMISSIONS[pathname]) {
    return ROUTE_PERMISSIONS[pathname];
  }
  
  // Check prefix match for nested routes (e.g., /clientes/123)
  for (const [route, key] of Object.entries(ROUTE_PERMISSIONS)) {
    if (pathname.startsWith(route + "/")) {
      return key;
    }
  }
  
  return null;
}
